<template>
  <div>
    <div v-if="part === 1">
      <h2>{{ $t('PEER.Result.title1') }}</h2>
      <br />
      <p>{{ $t('PEER.Result.explanation') }}</p>
    </div>
    <div v-if="part === 2">
      <h2>{{ $t('PEER.Result.title2') }}</h2>
    </div>
    <ResultTable
      v-if="part === 1"
      @updateSelectedResultId="updateSelectedResultId"
      @updateCurrentNumberOfReviewers="updateCurrentNumberOfReviewers"
    />
    <ResultDetailTable
      v-if="part === 2"
      :selectedResultId="selectedResultId"
      @updateRawData="updateRawData"
    />
    <div :class="`result-control flex-row ${part === 1 ? 'flex-spbt' : 'flex-right'}`">
      <ResultOptionInfo v-if="part === 1" />
      <div class="button-wrapper">
        <button v-if="part === 1" @click="movePage">{{ $t('PEER.Result.next') }}</button>
        <button v-else @click="movePage">{{ $t('PEER.Result.before') }}</button>
        <button v-if="part === 2" @click="download">{{ $t('PEER.Result.download') }}</button>
      </div>
    </div>
    <Modal v-if="isShow" />
  </div>
</template>

<script>
import ResultTable from '@/components/Peer/Result/ResultTable.vue';
import ResultOptionInfo from '@/components/Peer/Result/ResultOptionInfo.vue';
import ResultDetailTable from '@/components/Peer/Result/ResultDetailTable.vue';
import Modal from '@/components/Modal.vue';
import { mapActions, mapState } from 'vuex';

export default {
  components: {
    ResultTable,
    ResultOptionInfo,
    ResultDetailTable,
    Modal,
  },
  data() {
    return {
      part: 1,
      selectedResultId: 0,
      currentNumberOfReviewers: 0,
      rawData: [],
      formattedData: [],
    };
  },
  methods: {
    ...mapActions('modal', ['AC_MODAL_OPEN']),
    modalOpener() {
      const content =
        this.$i18n.locale === 'eng' ? 'No assessments selected.' : '선택된 평가지가 없습니다.';
      this.AC_MODAL_OPEN({
        content: content,
      });
    },
    checkSelectedResultId() {
      this.selectedResultId > 0 ? (this.part += 1) : this.modalOpener();
    },
    updateSelectedResultId(number) {
      this.selectedResultId = number;
    },
    movePage() {
      if (this.part === 1) {
        this.checkSelectedResultId();
      } else {
        this.part -= 1;
        this.updateSelectedResultId(0);
      }
    },
    updateRawData(rawData) {
      this.rawData = rawData;
    },
    setColumns(numberOfEvaluations, questionId) {
      const feedbackColumn = Array.from({ length: numberOfEvaluations }, (_, index) => [
        `평가자${index + 1}`,
        `평가자${index + 1} 점수`,
        `평가자${index + 1} 피드백`,
      ]).flat();

      const columnList = [
        'id',
        `question ${questionId}`,
        ...feedbackColumn,
        '평균 점수',
        '등수',
        '극단값 제거',
        '고성취 중심',
      ];

      return columnList;
    },
    setRowData(rows, numberOfEvaluations, totalNumberOfReviewers) {
      const lower30PercentStudents = this.lower30PercentStudents(rows, totalNumberOfReviewers);

      const rowData = rows.map((row) => {
        const { email: userIdx, content: answer, averageScore, ranking, reviews } = row;
        const flattedReviews = reviews.flatMap((review) => [
          review.email ?? '',
          review.score ?? '',
          review.feedbackAnswer ?? '',
        ]);

        const paddingLength = (numberOfEvaluations - 1) * 3 - flattedReviews.length;
        if (paddingLength > 0) flattedReviews.push(...Array(paddingLength).fill(' '));
        const defaultRowData = [userIdx, answer, ...flattedReviews, averageScore, ranking];

        defaultRowData.push(this.exceptOutlier(reviews));

        defaultRowData.push(this.focusHighAchievement(lower30PercentStudents, reviews));

        return defaultRowData;
      });

      return rowData;
    },
    exceptOutlier(reviews) {
      const sortedScore = reviews
        .filter((review) => review.score !== null)
        .map((review) => review.score)
        .sort((a, b) => {
          return a - b;
        });
      const length = sortedScore.length;

      if (length >= 3) {
        const exceptedOutlierArray = sortedScore.slice(1, length - 1);
        const exceptedOutlierScore =
          length - 2 > 1
            ? exceptedOutlierArray.reduce((a, b) => a + b) / (length - 2)
            : exceptedOutlierArray.pop();

        return exceptedOutlierScore ? Number.parseFloat(exceptedOutlierScore).toFixed(1) : '';
      } else {
        return '';
      }
    },
    lower30PercentStudents(rows, totalNumberOfReviewers) {
      const lower30PercentRank = Math.ceil(totalNumberOfReviewers - totalNumberOfReviewers * 0.3);
      return rows.slice(lower30PercentRank - 1, rows.length);
    },
    focusHighAchievement(lower30PercentStudents, reviews) {
      if (lower30PercentStudents.length === 0) {
        return '';
      }

      const highAchievementScores = reviews
        .filter((review) => !lower30PercentStudents.includes(review.email) && review.score !== null)
        .map((review) => review.score);

      const exceptedOutlierScore =
        highAchievementScores.length > 1
          ? highAchievementScores.reduce((a, b) => a + b) / highAchievementScores.length
          : highAchievementScores.pop();

      return Number.parseFloat(exceptedOutlierScore).toFixed(1);
    },
    createExcelFile(rawData) {
      const { title, numberOfEvaluations, totalNumberOfReviewers, questions } = rawData;
      const workbook = XLSX.utils.book_new();

      for (const response of questions) {
        const { answers } = response;
        const questionId = questions.indexOf(response) + 1;

        const columnList = this.setColumns(numberOfEvaluations, questionId);

        const rowData = this.setRowData(answers, numberOfEvaluations, totalNumberOfReviewers);

        let sheetData = [columnList, ...rowData];

        const sheet = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet, `Sheet ${questionId}`);
      }

      XLSX.writeFile(workbook, `[동료평가 결과] ${title}.xlsx`);
    },
    download() {
      this.createExcelFile(this.rawData);
    },
    updateCurrentNumberOfReviewers(number) {
      this.currentNumberOfReviewers = number;
    },
  },
  computed: {
    ...mapState('modal', ['isShow']),
    ...mapState('login', ['isLogin']),
  },
  created() {
    if (!this.isLogin) {
      const content =
        this.$i18n.locale === 'eng' ? 'Please sign in to use.' : '로그인이 필요한 서비스입니다.';
      const url = '/login';
      this.AC_MODAL_OPEN({
        content: content,
        url: url,
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.result-control {
  max-width: 1200px;
  align-items: flex-end;
  margin: 20px auto;
}
.button-wrapper {
  margin: 0;
  justify-content: right;
}

.flex-right {
  justify-content: right;
}
</style>
